/**
 * Listado único de productos. La fila abre la ficha de proveedor o la de merch.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { FileUp } from 'lucide-react';
import { AdminConfirmDialog } from '@/components/shared/AdminConfirmDialog';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { ResourceIndex, type ResourceIndexColumn } from '@/components/shared/ResourceIndex';
import {
    parseResourceIndexQuery,
    selectPageIds,
    toggleSelectedId,
    writeResourceIndexQuery,
    type ResourceIndexQuery,
} from '@/components/shared/resourceIndexState';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/hooks/useAuth';
import { csvCell } from '@/lib/csv';
import { formatDate, formatEur, formatNumber } from '@/lib/format-es';
import { notifyErr, notifyOk } from '@/lib/notify';
import { rememberStoreProductsListSearch } from '@/lib/store-products-return';
import { STATUS_TONE_VARIANT, storeStatusLabel } from '@/lib/store-status';
import { catalogImageUrl } from '../fichas/lib/catalogImageUrl';
import {
    PRODUCT_INDEX_URL,
    PRODUCT_PAGE_SIZE,
    PRODUCT_SELECT_CAP,
    PRODUCT_SORTS,
    PRODUCT_VIEW_LABELS,
    PRODUCT_VIEWS,
    STOCK_FILTERS,
    marginEuros,
    publicationStatus,
    productOriginAlias,
    stockLabel,
    type ProductViewId,
} from './productListModel';
import { useBatchSetCatalogAvailability } from '../catalog/hooks/useStoreCatalog';
import {
    fetchStoreProducts,
    useCatalogCollectionTitles,
    useSetStoreProductsBatch,
    useStoreProductsList,
    type ProductBatchAction,
    type StoreProductRow,
} from './useStoreProducts';

type BulkAction = ProductBatchAction | 'export' | 'availability';

function FilterMenu({
    label,
    ariaLabel,
    value,
    options,
    onChange,
}: {
    label: string;
    ariaLabel: string;
    value?: string;
    options: Array<{ value: string; label: string }>;
    onChange: (value: string | undefined) => void;
}) {
    const current = options.find((option) => option.value === value);
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button type="button" variant="outline" size="sm" aria-label={ariaLabel}>
                    {current?.label ?? label}
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="max-h-72 overflow-y-auto">
                <DropdownMenuItem onSelect={() => onChange(undefined)}>{label}</DropdownMenuItem>
                {options.map((option) => (
                    <DropdownMenuItem key={option.value} onSelect={() => onChange(option.value)}>
                        {option.label}
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

const BULK_COPY: Record<ProductBatchAction, { title: string; confirm: string; noun: string }> = {
    publish: { title: 'Publicar fichas', confirm: 'Publicar', noun: 'fichas' },
    review: { title: 'Pasar a revisión', confirm: 'Pasar a revisión', noun: 'fichas' },
    draft: { title: 'Pasar a borrador', confirm: 'Pasar a borrador', noun: 'fichas' },
    activate: { title: 'Activar productos', confirm: 'Activar', noun: 'productos' },
    deactivate: { title: 'Desactivar productos', confirm: 'Desactivar', noun: 'productos' },
};

function downloadCsv(filename: string, csv: string) {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
}

function priceLabel(row: StoreProductRow): string {
    if (row.priceMin == null) return '—';
    if (row.priceMax != null && row.priceMax !== row.priceMin) {
        return `${formatEur(row.priceMin)} – ${formatEur(row.priceMax)}`;
    }
    return formatEur(row.priceMin);
}

function marginLabel(row: StoreProductRow): string {
    const margin = marginEuros(row.priceMin, row.priceMax, row.costPrice);
    if (!margin) return '—';
    if (margin.min === margin.max) return formatEur(margin.min);
    return `${formatEur(margin.min)} – ${formatEur(margin.max)}`;
}

function completenessLabel(row: StoreProductRow): string {
    if (row.kind === 'merch' || row.completeness == null) return '—';
    return `${row.completeness}/4`;
}

function ProductThumb({ row }: { row: StoreProductRow }) {
    const src = catalogImageUrl(row.imageBlobPath, row.imagePrincipal);
    if (!src) {
        return <div className="h-10 w-10 rounded-lg border border-dashed border-border bg-muted/40" />;
    }
    return (
        <img src={src} alt="" className="h-10 w-10 rounded-lg border border-border object-contain bg-card" />
    );
}

export function StoreProductsPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const originAlias = productOriginAlias(params);
    const query = useMemo(() => {
        const parsed = parseResourceIndexQuery(params, PRODUCT_INDEX_URL);
        if (!parsed.filters.origin && originAlias.origin) {
            return { ...parsed, filters: { ...parsed.filters, origin: originAlias.origin } };
        }
        return parsed;
    }, [params, originAlias.origin]);
    const listParams = useMemo(
        () => ({
            view: query.view,
            q: query.q,
            page: query.page,
            sort: query.sort,
            dir: query.dir,
            origin: query.filters.origin,
            brand: query.filters.brand,
            collection: query.filters.collection,
            stock: query.filters.stock,
        }),
        [query],
    );
    const list = useStoreProductsList(listParams);
    const collectionTitles = useCatalogCollectionTitles();
    const batch = useSetStoreProductsBatch();
    const availabilityBatch = useBatchSetCatalogAvailability();
    const [availabilityDraft, setAvailabilityDraft] = useState<{ code: string; days: string }>({
        code: 'on_order',
        days: '10',
    });
    const rows = useMemo(() => list.data?.rows ?? [], [list.data]);
    const total = list.data?.total ?? 0;
    const counts = list.data?.counts;

    const [searchDraft, setSearchDraft] = useState(query.q);
    const [prevQ, setPrevQ] = useState(query.q);
    if (query.q !== prevQ) {
        setPrevQ(query.q);
        setSearchDraft(query.q);
    }

    const scope = `${query.view}|${query.q}|${query.filters.origin ?? ''}|${query.filters.brand ?? ''}|${query.filters.collection ?? ''}|${query.filters.stock ?? ''}|${query.sort ?? ''}|${query.dir ?? ''}`;
    const [scopeKey, setScopeKey] = useState(scope);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    if (scope !== scopeKey) {
        setScopeKey(scope);
        setSelectedIds([]);
    }

    const [selectingView, setSelectingView] = useState(false);
    const [pendingAction, setPendingAction] = useState<BulkAction | null>(null);
    const cacheRef = useRef(new Map<string, StoreProductRow>());

    useEffect(() => {
        for (const row of rows) cacheRef.current.set(row.id, row);
    }, [rows]);

    const listSearch = params.toString();
    useEffect(() => {
        if (!originAlias.dropSupplier) return;
        const next = new URLSearchParams(params);
        if (originAlias.origin) next.set('origin', originAlias.origin);
        next.delete('supplier');
        setParams(next, { replace: true });
    }, [originAlias.dropSupplier, originAlias.origin, params, setParams]);
    useEffect(() => {
        const handle = window.setTimeout(() => {
            if (searchDraft.trim() === query.q.trim()) return;
            const next = writeResourceIndexQuery(
                params,
                { ...query, q: searchDraft, page: 1 },
                PRODUCT_INDEX_URL,
            );
            setParams(next);
        }, 300);
        return () => window.clearTimeout(handle);
    }, [searchDraft, query, params, setParams]);

    function commit(next: ResourceIndexQuery) {
        const written = writeResourceIndexQuery(params, next, PRODUCT_INDEX_URL);
        if (written.toString() !== params.toString()) setParams(written);
    }

    function setFilter(key: string, value: string | undefined) {
        const filters = { ...query.filters };
        if (value) filters[key] = value;
        else delete filters[key];
        commit({ ...query, filters, page: 1 });
    }

    const known = new Map(cacheRef.current);
    for (const row of rows) known.set(row.id, row);
    const knownSelected = selectedIds
        .map((id) => known.get(id))
        .filter((row): row is StoreProductRow => Boolean(row));
    const missingSelected = selectedIds.length - knownSelected.length;
    const catalogIds = knownSelected.filter((row) => row.kind === 'catalog').map((row) => row.sourceId);
    const merchIds = knownSelected.filter((row) => row.kind === 'merch').map((row) => row.sourceId);
    const fichaReason = batch.isPending
        ? 'Aplicando…'
        : missingSelected > 0
            ? 'Algunos productos seleccionados no están cargados'
            : 'El merch no tiene estado de ficha. Quita esos productos de la selección.';
    const fichaBlocked = batch.isPending || missingSelected > 0 || merchIds.length > 0 || catalogIds.length === 0;
    const activeBlocked = batch.isPending || missingSelected > 0 || knownSelected.length === 0;

    const columns = useMemo<ResourceIndexColumn<StoreProductRow>[]>(() => [
        {
            id: 'name',
            header: 'Producto',
            cell: (row) => (
                <span className="flex items-center gap-3">
                    <ProductThumb row={row} />
                    <span className="min-w-0">
                        <span className="block font-medium text-foreground">{row.name}</span>
                        <span className="block text-xs text-muted-foreground">{row.slug}</span>
                    </span>
                </span>
            ),
        },
        {
            id: 'status',
            header: 'Publicación',
            sortable: false,
            cell: (row) => <StatusBadge status={publicationStatus(row)} />,
        },
        {
            id: 'completeness',
            header: 'Ficha',
            cell: (row) => <span className="tabular-nums">{completenessLabel(row)}</span>,
        },
        {
            id: 'origin',
            header: 'Origen',
            sortable: false,
            cell: (row) => row.originLabel || '—',
        },
        {
            id: 'brand',
            header: 'Marca',
            sortable: false,
            cell: (row) => row.brand || '—',
        },
        {
            id: 'stock',
            header: 'Stock',
            sortable: false,
            cell: (row) => stockLabel(row.stockCode, row.stockUnits),
        },
        {
            id: 'price',
            header: 'Precio',
            align: 'right',
            cell: (row) => <span className="tabular-nums">{priceLabel(row)}</span>,
        },
        {
            id: 'margin',
            header: 'Margen',
            align: 'right',
            sortable: false,
            cell: (row) => <span className="tabular-nums">{marginLabel(row)}</span>,
        },
        {
            id: 'sync',
            header: 'Última sync',
            cell: (row) => {
                if (row.kind === 'merch') return '—';
                return (
                    <span className="flex flex-col items-start gap-1">
                        <span className="tabular-nums text-muted-foreground">
                            {row.lastSyncAt ? formatDate(row.lastSyncAt) : 'Sin sync'}
                        </span>
                        {row.syncStale ? (
                            <StatusBadge
                                status="critico"
                                variant={STATUS_TONE_VARIANT.critico}
                                label="Caducada"
                            />
                        ) : null}
                    </span>
                );
            },
        },
    ], []);

    const origins = list.data?.origins ?? [];
    const chips = [
        query.filters.origin
            ? { id: 'origin', label: `Origen: ${origins.find((item) => item.key === query.filters.origin)?.label ?? query.filters.origin}` }
            : null,
        query.filters.brand ? { id: 'brand', label: `Marca: ${query.filters.brand}` } : null,
        query.filters.collection
            ? {
                id: 'collection',
                label: `Colección: ${collectionTitles.data?.find((item) => item.slug === query.filters.collection)?.title ?? query.filters.collection}`,
            }
            : null,
        query.filters.stock
            ? { id: 'stock', label: `Stock: ${STOCK_FILTERS.find((item) => item.id === query.filters.stock)?.label ?? query.filters.stock}` }
            : null,
    ].filter((chip): chip is { id: string; label: string } => Boolean(chip));

    async function selectEntireView() {
        if (!user?.id) return;
        setSelectingView(true);
        try {
            const cap = Math.min(total, PRODUCT_SELECT_CAP);
            const result = await fetchStoreProducts(user.id, {
                ...listParams,
                page: 1,
                limit: cap,
                offset: 0,
            });
            for (const row of result.rows) cacheRef.current.set(row.id, row);
            setSelectedIds(result.rows.map((row) => row.id));
            if (total > PRODUCT_SELECT_CAP) {
                notifyOk(`Seleccionados los primeros ${formatNumber(PRODUCT_SELECT_CAP)}`);
            }
        } catch (error: unknown) {
            notifyErr(error instanceof Error ? error.message : 'No se ha podido seleccionar la vista');
        } finally {
            setSelectingView(false);
        }
    }

    function openRow(row: StoreProductRow) {
        const search = listSearch ? `?${listSearch}` : '';
        rememberStoreProductsListSearch(search);
        if (row.kind === 'merch') {
            navigate(`/store/products/merch/${row.sourceId}`, { state: { productsListSearch: search } });
            return;
        }
        navigate(`/store/products/${row.sourceId}`, { state: { productsListSearch: search } });
    }

    async function runBatch(action: ProductBatchAction) {
        try {
            const touchesMerch = action === 'activate' || action === 'deactivate';
            await batch.mutateAsync({
                action,
                catalogIds,
                merchIds: touchesMerch ? merchIds : [],
            });
            notifyOk('Cambio aplicado');
            setSelectedIds([]);
            setPendingAction(null);
        } catch (error: unknown) {
            notifyErr(error instanceof Error ? error.message : 'No se ha podido aplicar el cambio');
        }
    }

    function exportSelected() {
        const headers = ['Nombre', 'Publicación', 'Ficha', 'Origen', 'Marca', 'Stock', 'Precio', 'Margen', 'Última sync'];
        const lines = knownSelected.map((row) => [
            row.name,
            storeStatusLabel(publicationStatus(row)),
            completenessLabel(row),
            row.originLabel,
            row.brand,
            stockLabel(row.stockCode, row.stockUnits),
            priceLabel(row),
            marginLabel(row),
            row.kind === 'merch' ? '' : (row.lastSyncAt ? formatDate(row.lastSyncAt) : ''),
        ]);
        const csv = `\uFEFF${[headers, ...lines].map((line) => line.map(csvCell).join(';')).join('\n')}`;
        downloadCsv('productos-tienda.csv', csv);
        setPendingAction(null);
    }

    async function runAvailabilityBatch() {
        try {
            const days = availabilityDraft.code === 'on_order' ? Number(availabilityDraft.days) : null;
            if (availabilityDraft.code === 'on_order' && (days == null || !Number.isInteger(days) || days < 0 || days > 180)) {
                notifyErr('Indica un plazo en días');
                return;
            }
            await availabilityBatch.mutateAsync({
                catalogIds,
                disponibilidad: availabilityDraft.code,
                leadTimeDays: days,
            });
            notifyOk('Disponibilidad aplicada');
            setSelectedIds([]);
            setPendingAction(null);
        } catch (error: unknown) {
            notifyErr(error instanceof Error ? error.message : 'No se ha podido aplicar el cambio');
        }
    }

    const pendingCopy = pendingAction && pendingAction !== 'export' && pendingAction !== 'availability'
        ? BULK_COPY[pendingAction]
        : null;
    const affected = pendingAction === 'export'
        ? knownSelected.length
        : pendingAction === 'activate' || pendingAction === 'deactivate'
            ? catalogIds.length + merchIds.length
            : catalogIds.length;

    return (
        <>
            <ResourceIndex
                actions={
                    <>
                        <Button type="button" variant="outline" size="sm" asChild>
                            <Link to="/store/products/import">
                                <FileUp size={14} />
                                Importar fichas
                            </Link>
                        </Button>
                        <Button type="button" variant="outline" size="sm" asChild>
                            <Link to="/store/products/stock">Stock de merch</Link>
                        </Button>
                    </>
                }
                views={PRODUCT_VIEWS.map((id) => ({
                    id,
                    label: PRODUCT_VIEW_LABELS[id as ProductViewId],
                    count: counts?.[id],
                }))}
                view={query.view}
                onViewChange={(id) => commit({ ...query, view: id, page: 1 })}
                search={searchDraft}
                onSearchChange={setSearchDraft}
                searchPlaceholder="Nombre, referencia, EAN o slug"
                filters={
                    <>
                        <FilterMenu
                            label="Todos los orígenes"
                            ariaLabel="Origen"
                            value={query.filters.origin}
                            options={origins.map((origin) => ({ value: origin.key, label: origin.label }))}
                            onChange={(value) => setFilter('origin', value)}
                        />
                        <FilterMenu
                            label="Todas las marcas"
                            ariaLabel="Marca"
                            value={query.filters.brand}
                            options={(list.data?.brands ?? []).map((brand) => ({ value: brand, label: brand }))}
                            onChange={(value) => setFilter('brand', value)}
                        />
                        <FilterMenu
                            label="Todas las colecciones"
                            ariaLabel="Colección"
                            value={query.filters.collection}
                            options={(list.data?.collections ?? []).map((slug) => ({
                                value: slug,
                                label: collectionTitles.data?.find((item) => item.slug === slug)?.title ?? slug,
                            }))}
                            onChange={(value) => setFilter('collection', value)}
                        />
                        <FilterMenu
                            label="Todo el stock"
                            ariaLabel="Stock"
                            value={query.filters.stock}
                            options={STOCK_FILTERS.map((item) => ({ value: item.id, label: item.label }))}
                            onChange={(value) => setFilter('stock', value)}
                        />
                    </>
                }
                chips={chips}
                onRemoveChip={(id) => setFilter(id, undefined)}
                columns={columns}
                rows={rows}
                rowId={(row) => row.id}
                sort={query.sort ?? 'name'}
                sortDir={query.dir === 'desc' ? 'desc' : 'asc'}
                onSort={(columnId) => {
                    if (!(PRODUCT_SORTS as readonly string[]).includes(columnId)) return;
                    const current = query.sort ?? 'name';
                    const currentDir = query.dir ?? 'asc';
                    const dir = current === columnId && currentDir !== 'desc' ? 'desc' : 'asc';
                    const isDefault = columnId === 'name' && dir === 'asc';
                    commit({
                        ...query,
                        sort: isDefault ? null : columnId,
                        dir: isDefault ? null : dir,
                        page: 1,
                    });
                }}
                selectedIds={selectedIds}
                onToggleRow={(id) => setSelectedIds((current) => toggleSelectedId(current, id))}
                onTogglePage={(checked) => setSelectedIds((current) => selectPageIds(current, rows.map((row) => row.id), checked))}
                onSelectView={() => {
                    void selectEntireView();
                }}
                selectViewPending={selectingView}
                bulkActions={[
                    {
                        id: 'publish',
                        label: 'Publicar',
                        disabled: fichaBlocked,
                        disabledReason: fichaReason,
                        onClick: () => setPendingAction('publish'),
                    },
                    {
                        id: 'review',
                        label: 'Pasar a revisión',
                        disabled: fichaBlocked,
                        disabledReason: fichaReason,
                        onClick: () => setPendingAction('review'),
                    },
                    {
                        id: 'draft',
                        label: 'Pasar a borrador',
                        disabled: fichaBlocked,
                        disabledReason: fichaReason,
                        onClick: () => setPendingAction('draft'),
                    },
                    {
                        id: 'activate',
                        label: 'Activar',
                        disabled: activeBlocked || batch.isPending,
                        disabledReason: batch.isPending ? 'Aplicando…' : 'Selecciona productos cargados',
                        onClick: () => setPendingAction('activate'),
                    },
                    {
                        id: 'deactivate',
                        label: 'Desactivar',
                        disabled: activeBlocked || batch.isPending,
                        disabledReason: batch.isPending ? 'Aplicando…' : 'Selecciona productos cargados',
                        onClick: () => setPendingAction('deactivate'),
                    },
                    {
                        id: 'availability',
                        label: 'Fijar disponibilidad',
                        disabled: fichaBlocked,
                        disabledReason: fichaReason,
                        onClick: () => setPendingAction('availability'),
                    },
                    {
                        id: 'export',
                        label: 'Exportar CSV',
                        disabled: knownSelected.length === 0,
                        disabledReason: 'Los productos seleccionados no están cargados en esta página',
                        onClick: () => setPendingAction('export'),
                    },
                ]}
                page={query.page}
                pageSize={PRODUCT_PAGE_SIZE}
                total={total}
                onPageChange={(page) => commit({ ...query, page })}
                isLoading={list.isLoading}
                error={list.error instanceof Error ? list.error.message : null}
                emptyTitle={list.isError ? 'No se ha podido cargar el listado' : 'No hay productos en esta vista'}
                emptyDescription={list.isError ? undefined : 'Prueba con otra vista o quita algún filtro.'}
                onRowClick={openRow}
            />
            <AdminConfirmDialog
                open={pendingAction != null}
                title={
                    pendingAction === 'export'
                        ? 'Exportar CSV'
                        : pendingAction === 'availability'
                            ? 'Fijar disponibilidad'
                            : pendingCopy?.title ?? 'Confirmar'
                }
                confirmLabel={
                    pendingAction === 'export'
                        ? 'Exportar'
                        : pendingAction === 'availability'
                            ? 'Aplicar'
                            : pendingCopy?.confirm
                }
                affectedCount={affected}
                affectedNoun={pendingAction === 'export' || pendingAction === 'activate' || pendingAction === 'deactivate' ? 'productos' : 'fichas'}
                confirmDisabled={batch.isPending || availabilityBatch.isPending}
                description={
                    pendingAction === 'availability' ? (
                        <div className="space-y-2 text-sm">
                            <label className="block space-y-1">
                                <span>Disponibilidad</span>
                                <select
                                    className="h-9 w-full rounded-md border border-input bg-background px-2"
                                    value={availabilityDraft.code}
                                    onChange={(event) => setAvailabilityDraft((prev) => ({ ...prev, code: event.target.value }))}
                                >
                                    <option value="in_stock">En stock</option>
                                    <option value="on_order">Bajo pedido</option>
                                    <option value="out_of_stock">Agotado</option>
                                </select>
                            </label>
                            {availabilityDraft.code === 'on_order' ? (
                                <label className="block space-y-1">
                                    <span>Plazo (días)</span>
                                    <input
                                        className="h-9 w-full rounded-md border border-input bg-background px-2"
                                        inputMode="numeric"
                                        value={availabilityDraft.days}
                                        onChange={(event) => setAvailabilityDraft((prev) => ({ ...prev, days: event.target.value }))}
                                    />
                                </label>
                            ) : null}
                        </div>
                    ) : undefined
                }
                onCancel={() => setPendingAction(null)}
                onConfirm={() => {
                    if (pendingAction === 'export') {
                        exportSelected();
                        return;
                    }
                    if (pendingAction === 'availability') {
                        void runAvailabilityBatch();
                        return;
                    }
                    if (pendingAction) void runBatch(pendingAction);
                }}
            />
        </>
    );
}
